import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CloudOff as CloudOffIcon,
  OpenInNew as OpenInNewIcon,
  Mic as MicIcon,
  Description as DescriptionIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  TextSnippet as TextSnippetIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, WedmsStatusChip, SttStatusChip } from '../../components/common';
import { useAuthStore } from '../../hooks/useAuth';
import { documentService, recordingService, WeDMSFile } from '../../services/documentService';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type FileTypeFilter = 'all' | 'recording' | 'document';

const DocumentListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FileTypeFilter>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<WeDMSFile | null>(null);
  const [deleteResult, setDeleteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [checkingSTTId, setCheckingSTTId] = useState<number | null>(null);

  // 녹음 파일 업로드 상태
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // STT 텍스트 미리보기 상태
  const [sttPreviewOpen, setSttPreviewOpen] = useState(false);
  const [sttPreviewFile, setSttPreviewFile] = useState<WeDMSFile | null>(null);
  const [sttPreviewText, setSttPreviewText] = useState<string>('');
  const [loadingSttPreview, setLoadingSttPreview] = useState(false);

  // STT 에러 보기 다이얼로그 상태
  const [sttErrorOpen, setSttErrorOpen] = useState(false);
  const [sttErrorFile, setSttErrorFile] = useState<WeDMSFile | null>(null);
  const [sttErrorText, setSttErrorText] = useState<string>('');
  const [loadingSttError, setLoadingSttError] = useState(false);

  // 통합 문서 목록 조회
  const { data: files = [], isLoading, error, refetch } = useQuery({
    queryKey: ['all-files'],
    queryFn: () => documentService.getAllFiles(),
  });

  // 처리 중인 파일 자동 폴링 (녹음 STT + 문서 WeDMS)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 처리 중인 녹음 파일 (STT)
    const processingRecordings = files.filter(
      (f) => f.type === 'recording' && (f.stt_status === 'pending' || f.stt_status === 'processing')
    );

    // 처리 중인 문서 (WeDMS)
    const processingDocuments = files.filter(
      (f) => f.type === 'document' && (f.wedms_status === 'pending' || f.wedms_status === 'processing')
    );

    const hasProcessingFiles = processingRecordings.length > 0 || processingDocuments.length > 0;

    // 처리 중인 파일이 없으면 폴링 중지
    if (!hasProcessingFiles) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // 이미 폴링 중이면 스킵
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(async () => {
      let shouldRefetch = false;

      // 녹음 파일 STT 상태 확인
      for (const file of processingRecordings) {
        try {
          const result = await recordingService.getSTTStatus(file.id);
          if (result.status === 'completed' || result.status === 'failed' || result.wedms_doc_id) {
            shouldRefetch = true;
          }
        } catch (error: unknown) {
          // 401 에러 시 폴링 중단 (인증 만료)
          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status?: number } };
            if (axiosError.response?.status === 401) {
              console.warn('Authentication expired, stopping polling');
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              return;
            }
          }
          console.error('Failed to check STT status:', error);
        }
      }

      // 처리 중인 문서가 있으면 목록 새로고침
      if (processingDocuments.length > 0) {
        shouldRefetch = true;
      }

      if (shouldRefetch) {
        refetch();
      }
    }, 5000); // 5초 간격으로 폴링

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [files, refetch]);

  // 문서 삭제
  const deleteDocumentMutation = useMutation({
    mutationFn: (id: number) => documentService.delete(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['all-files'] });
      setDeleteDialogOpen(false);
      const message = response?.message || '문서가 삭제되었습니다.';
      setDeleteResult({ type: 'success', message });
      setSelectedFile(null);
    },
    onError: () => {
      setDeleteResult({ type: 'error', message: '문서 삭제에 실패했습니다.' });
    },
  });

  // 녹음 파일 삭제
  const deleteRecordingMutation = useMutation({
    mutationFn: (id: number) => recordingService.delete(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['all-files'] });
      setDeleteDialogOpen(false);
      const message = response?.message || '녹음 파일이 삭제되었습니다.';
      setDeleteResult({ type: 'success', message });
      setSelectedFile(null);
    },
    onError: () => {
      setDeleteResult({ type: 'error', message: '녹음 파일 삭제에 실패했습니다.' });
    },
  });

  // 필터링
  const filteredFiles = files.filter((file) => {
    // 타입 필터
    if (typeFilter !== 'all' && file.type !== typeFilter) return false;

    // 검색 필터
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.filename.toLowerCase().includes(query) ||
      file.client_name?.toLowerCase().includes(query) ||
      file.template_name?.toLowerCase().includes(query)
    );
  });

  // 문서 다운로드
  const handleDownload = async (file: WeDMSFile) => {
    try {
      let blob: Blob;
      if (file.type === 'document') {
        blob = await documentService.download(file.id);
      } else {
        blob = await recordingService.download(file.id);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      setDeleteResult({ type: 'error', message: '다운로드에 실패했습니다.' });
    }
  };

  // 삭제 확인 다이얼로그
  const handleDeleteClick = (file: WeDMSFile) => {
    setSelectedFile(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedFile) {
      if (selectedFile.type === 'document') {
        deleteDocumentMutation.mutate(selectedFile.id);
      } else {
        deleteRecordingMutation.mutate(selectedFile.id);
      }
    }
  };

  // 상담 기록으로 이동
  const handleViewConsultation = (consultationId: number) => {
    navigate(`/consultations/${consultationId}/edit`);
  };

  // STT 상태 확인 (녹음 파일)
  const handleCheckSTTStatus = async (recordingId: number) => {
    setCheckingSTTId(recordingId);
    try {
      const result = await recordingService.getSTTStatus(recordingId);
      if (result.status === 'completed') {
        setDeleteResult({ type: 'success', message: 'STT 처리가 완료되었습니다.' });
      } else if (result.status === 'processing') {
        const taskStatus = result.task_status ? ` (${result.task_status})` : '';
        const msg = result.message || 'STT 처리 중입니다. 잠시 후 다시 확인해주세요.';
        setDeleteResult({ type: 'success', message: msg + taskStatus });
      } else if (result.status === 'failed') {
        const errorMsg = result.message || 'STT 처리에 실패했습니다.';
        setDeleteResult({ type: 'error', message: errorMsg });
      }
      // 목록 새로고침
      queryClient.invalidateQueries({ queryKey: ['all-files'] });
    } catch (err) {
      console.error('STT status check failed:', err);
      setDeleteResult({ type: 'error', message: 'STT 상태 확인에 실패했습니다.' });
    } finally {
      setCheckingSTTId(null);
    }
  };

  // WeDMS에서 보기 (외부 링크)
  const handleViewInWeDMS = (wedmsDocId: string) => {
    window.open(`http://wedms.widus-it.com/documents/${wedmsDocId}/`, '_blank');
  };

  // 녹음 파일 업로드 핸들러
  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedUploadFile(file);
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedUploadFile) return;

    setIsUploading(true);
    try {
      // 상담 기록 없이 녹음 파일만 업로드 (standalone)
      const result = await recordingService.uploadStandalone(selectedUploadFile);

      setDeleteResult({ type: 'success', message: result.message || '녹음 파일이 업로드되었습니다. STT 처리가 시작됩니다.' });
      setUploadDialogOpen(false);
      setSelectedUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['all-files'] });
    } catch (err: unknown) {
      console.error('Upload failed:', err);
      // 에러 메시지 추출
      let errorMessage = '업로드에 실패했습니다.';
      if (typeof err === 'object' && err !== null) {
        const errorObj = err as {
          response?: { data?: { message?: string; detail?: string }; status?: number };
          message?: string;
          code?: string;
        };
        if (errorObj.response?.data?.message) {
          errorMessage += ` (${errorObj.response.data.message})`;
        } else if (errorObj.response?.data?.detail) {
          errorMessage += ` (${errorObj.response.data.detail})`;
        } else if (errorObj.code === 'ERR_NETWORK' || errorObj.message?.includes('Network Error')) {
          errorMessage += ' (서버에 연결할 수 없습니다)';
        } else if (errorObj.response?.status) {
          errorMessage += ` (HTTP ${errorObj.response.status})`;
        } else if (errorObj.message) {
          errorMessage += ` (${errorObj.message})`;
        }
      } else if (err instanceof Error) {
        errorMessage += ` (${err.message})`;
      }
      setDeleteResult({ type: 'error', message: errorMessage });
      // 실패해도 다이얼로그 닫고 상태 초기화
      setUploadDialogOpen(false);
      setSelectedUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setIsUploading(false);
    }
  };

  // STT 텍스트 미리보기
  const handleViewSTT = async (file: WeDMSFile) => {
    setSttPreviewFile(file);
    setSttPreviewOpen(true);
    setLoadingSttPreview(true);
    setSttPreviewText('');

    try {
      const result = await recordingService.getSTTStatus(file.id);
      if (result.stt_text) {
        setSttPreviewText(result.stt_text);
      } else if (result.status === 'processing') {
        setSttPreviewText('STT 처리 중입니다. 잠시 후 다시 확인해주세요.');
      } else {
        setSttPreviewText('STT 텍스트가 없습니다.');
      }
    } catch (err) {
      console.error('Failed to load STT text:', err);
      setSttPreviewText('STT 텍스트를 불러오는데 실패했습니다.');
    } finally {
      setLoadingSttPreview(false);
    }
  };


  // STT 에러 보기
  const handleViewSTTError = async (file: WeDMSFile) => {
    setSttErrorFile(file);
    setSttErrorOpen(true);
    setLoadingSttError(true);
    setSttErrorText('');

    try {
      // 캐시된 에러가 있으면 사용
      if (file.stt_error) {
        setSttErrorText(file.stt_error);
      } else {
        // API에서 가져오기
        const result = await recordingService.getSTTStatus(file.id);
        if (result.stt_error) {
          setSttErrorText(result.stt_error);
        } else if (result.message) {
          setSttErrorText(result.message);
        } else {
          setSttErrorText('STT 처리 중 알 수 없는 오류가 발생했습니다.');
        }
      }
    } catch (err) {
      console.error('Failed to load STT error:', err);
      setSttErrorText('에러 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoadingSttError(false);
    }
  };

  const isDeleting = deleteDocumentMutation.isPending || deleteRecordingMutation.isPending;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="문서 관리"
        breadcrumbs={[{ label: '문서 관리' }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          문서 목록을 불러오는데 실패했습니다.
        </Alert>
      )}

      {deleteResult && (
        <Alert
          severity={deleteResult.type}
          sx={{ mb: 2 }}
          onClose={() => setDeleteResult(null)}
        >
          {deleteResult.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* 필터 및 검색 */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="파일명, 고객명, 템플릿명으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              value={typeFilter}
              exclusive
              onChange={(_, value) => value && setTypeFilter(value)}
              size="small"
            >
              <ToggleButton value="all">
                <FilterListIcon sx={{ mr: 0.5 }} fontSize="small" />
                전체
              </ToggleButton>
              <ToggleButton value="recording">
                <MicIcon sx={{ mr: 0.5 }} fontSize="small" />
                녹음
              </ToggleButton>
              <ToggleButton value="document">
                <DescriptionIcon sx={{ mr: 0.5 }} fontSize="small" />
                문서
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
            >
              녹음 업로드
            </Button>

            {/* 숨겨진 파일 입력 */}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.m4a,.mp3,.wav,.ogg,.flac"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>

          {/* 문서 목록 테이블 */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>유형</TableCell>
                  <TableCell>파일명</TableCell>
                  <TableCell>고객명</TableCell>
                  <TableCell align="right">파일 크기</TableCell>
                  <TableCell align="center">WeDMS</TableCell>
                  <TableCell align="center">상태</TableCell>
                  <TableCell>생성일</TableCell>
                  <TableCell align="center">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchQuery || typeFilter !== 'all' ? '검색 결과가 없습니다.' : '업로드된 파일이 없습니다.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFiles.map((file) => (
                    <TableRow key={`${file.type}-${file.id}`} hover>
                      <TableCell>
                        {file.type === 'recording' ? (
                          <Chip
                            icon={<MicIcon />}
                            label="녹음"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<DescriptionIcon />}
                            label="문서"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {file.filename}
                        </Typography>
                        {file.template_name && (
                          <Typography variant="caption" color="text.secondary">
                            템플릿: {file.template_name} (v{file.version})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {file.client_name || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.consultation_date || (file.type === 'recording' && !file.consultation_id ? '(상담 미연결)' : '-')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.file_size)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <WedmsStatusChip
                          status={file.wedms_status}
                          docId={file.wedms_doc_id}
                          error={file.wedms_error}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {file.type === 'recording' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            {/* STT 상태 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SttStatusChip
                                status={file.stt_status}
                                error={file.stt_error}
                              />
                              {/* STT 실패 시 에러 보기 버튼 */}
                              {file.stt_status === 'failed' && (
                                <Tooltip title="에러 상세 보기">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleViewSTTError(file)}
                                  >
                                    <ErrorIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {file.stt_status !== 'completed' && file.stt_status !== 'failed' && (
                                <Tooltip title="STT 상태 새로고침">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCheckSTTStatus(file.id)}
                                    disabled={checkingSTTId === file.id}
                                  >
                                    {checkingSTTId === file.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <RefreshIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        )}
                        {file.type === 'document' && (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(file.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {/* 녹음 파일: STT 텍스트 보기 */}
                          {file.type === 'recording' && file.stt_status === 'completed' && (
                            <Tooltip title="STT 텍스트 보기">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleViewSTT(file)}
                              >
                                <TextSnippetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* 상담이 있는 경우에만 상담 보기 버튼 표시 */}
                          {file.consultation_id && (
                            <Tooltip title="상담 기록 보기">
                              <IconButton
                                size="small"
                                onClick={() => handleViewConsultation(file.consultation_id!)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="다운로드">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(file)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {file.wedms_doc_id && (
                            <Tooltip title="WeDMS에서 보기">
                              <IconButton
                                size="small"
                                onClick={() => handleViewInWeDMS(file.wedms_doc_id)}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="삭제">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(file)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 문서 수 표시 */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              총 {filteredFiles.length}개 파일
              {(searchQuery || typeFilter !== 'all') && ` (전체 ${files.length}개 중)`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                녹음: {files.filter(f => f.type === 'recording').length}개
              </Typography>
              <Typography variant="body2" color="text.secondary">
                문서: {files.filter(f => f.type === 'document').length}개
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {selectedFile?.type === 'recording' ? '녹음 파일 삭제' : '문서 삭제'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            &quot;{selectedFile?.filename}&quot; 파일을 삭제하시겠습니까?
          </Typography>
          {selectedFile?.wedms_doc_id && (
            <Alert severity="info" sx={{ mt: 2 }}>
              WeDMS에 업로드된 파일입니다. 삭제 시 WeDMS에서도 함께 삭제됩니다.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 녹음 파일 업로드 다이얼로그 */}
      <Dialog open={uploadDialogOpen} onClose={() => !isUploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>녹음 파일 업로드</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            녹음 파일을 업로드하면 WeDMS에 업로드되어 STT 처리가 시작됩니다.
            STT 처리 완료 후 상담 기록 화면에서 녹음 파일을 선택하여 AI 분석을 시작할 수 있습니다.
          </Typography>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: selectedUploadFile ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedUploadFile ? (
              <>
                <MicIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body1" fontWeight={500}>
                  {selectedUploadFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(selectedUploadFile.size)}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  클릭하여 파일 선택
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  지원 형식: MP3, M4A, WAV, OGG, FLAC
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedUploadFile(null); }} disabled={isUploading}>
            취소
          </Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={!selectedUploadFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          >
            {isUploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* STT 텍스트 미리보기 다이얼로그 */}
      <Dialog open={sttPreviewOpen} onClose={() => setSttPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextSnippetIcon color="info" />
            STT 텍스트
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              파일명: {sttPreviewFile?.filename}
            </Typography>
            {sttPreviewFile?.client_name && (
              <Typography variant="subtitle2" color="text.secondary">
                고객명: {sttPreviewFile.client_name}
              </Typography>
            )}
          </Box>
          {loadingSttPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: 400,
                overflow: 'auto',
                bgcolor: 'grey.50',
                whiteSpace: 'pre-wrap',
              }}
            >
              <Typography variant="body2">
                {sttPreviewText || 'STT 텍스트가 없습니다.'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSttPreviewOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* STT 에러 다이얼로그 */}
      <Dialog open={sttErrorOpen} onClose={() => setSttErrorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            STT 처리 실패
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              파일명: {sttErrorFile?.filename}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              업로드 시각: {sttErrorFile?.created_at ? formatDate(sttErrorFile.created_at) : '-'}
            </Typography>
          </Box>
          {loadingSttError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
              {sttErrorText || '알 수 없는 오류가 발생했습니다.'}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            WeDMS 연동 상태나 파일 형식을 확인해주세요. 문제가 지속되면 관리자에게 문의해주세요.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSttErrorOpen(false)}>닫기</Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setSttErrorOpen(false);
              if (sttErrorFile) {
                handleCheckSTTStatus(sttErrorFile.id);
              }
            }}
          >
            다시 확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentListPage;
